import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, AlertTriangle, RefreshCw, Mail, Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function AuthCallback() {
    const navigate = useNavigate();
    const { exchangeGoogleSession, requestMagicLink } = useAuth();
    const hasProcessed = useRef(false);
    const [errMsg, setErrMsg] = useState(null);
    // Inline magic-link fallback state (was a broken <Link to="/auth/magic">
    // which is the *verify* route — landing there without a token rendered
    // the "Link not valid · Missing or invalid link" screen).
    const [showMagicForm, setShowMagicForm] = useState(false);
    const [magicEmail, setMagicEmail] = useState("");
    const [magicSending, setMagicSending] = useState(false);
    const [magicSent, setMagicSent] = useState(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        const sessionId = match ? decodeURIComponent(match[1]) : null;

        if (!sessionId) {
            navigate("/", { replace: true });
            return;
        }

        // Retry the exchange a few times with backoff. Emergent's OAuth
        // session-data service occasionally returns 401 in the first ~3s
        // after a fresh sign-in because the session hasn't fully propagated
        // yet. Retrying avoids the user-visible "click Google 3 times" bug.
        const attemptExchange = async () => {
            const DELAYS_MS = [0, 700, 1500, 2500]; // total ~4.7s
            let lastError = null;
            for (let i = 0; i < DELAYS_MS.length; i += 1) {
                if (DELAYS_MS[i] > 0) {
                    await new Promise((r) => setTimeout(r, DELAYS_MS[i]));
                }
                try {
                    const user = await exchangeGoogleSession(sessionId);
                    return user;
                } catch (err) {
                    lastError = err;
                    const status = err?.response?.status;
                    // Only retry transient upstream/auth errors. A 400 (bad
                    // payload) or network meltdown won't get better on retry.
                    if (status && status !== 401 && status !== 502 && status !== 503) {
                        throw err;
                    }
                }
            }
            throw lastError || new Error("Session exchange failed after retries");
        };

        (async () => {
            try {
                const user = await attemptExchange();
                window.history.replaceState(null, "", window.location.pathname);
                const dest = user.role === "owner" || user.role === "coach" ? "/coach" : "/dashboard";
                navigate(dest, { replace: true, state: { user } });
            } catch (e) {
                // Strip the session_id from URL so the user can refresh-retry cleanly.
                window.history.replaceState(null, "", window.location.pathname);
                const detail = e?.response?.data?.detail
                    || "Couldn't complete Google sign-in. Please try again, or use the email sign-in option.";
                console.error("Google session exchange failed", e);
                setErrMsg(detail);
            }
        })();
    }, [exchangeGoogleSession, navigate]);

    const sendMagicLink = async (e) => {
        e.preventDefault();
        if (!magicEmail) return;
        setMagicSending(true);
        try {
            await requestMagicLink(magicEmail);
            setMagicSent(true);
        } catch (err) {
            const detail = err?.response?.data?.detail || "Could not send sign-in link.";
            toast.error("Sign-in failed", { description: detail });
        } finally {
            setMagicSending(false);
        }
    };

    if (errMsg) {
        return (
            <div
                data-testid="auth-callback-error"
                className="min-h-screen bg-brand-obsidian text-white flex flex-col items-center justify-center px-6 py-12"
            >
                <AlertTriangle className="w-10 h-10 text-amber-400 mb-4" />
                <div className="text-[10px] tracking-[0.3em] uppercase text-amber-400 mb-2">Sign-in didn't complete</div>
                <p className="text-sm text-zinc-300 max-w-md text-center mb-6">{errMsg}</p>

                {!showMagicForm && (
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            data-testid="auth-retry-google"
                            onClick={() => {
                                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                                const redirectUrl = window.location.origin + "/dashboard";
                                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                            }}
                            className="btn-tactical text-xs inline-flex items-center gap-2"
                        >
                            <RefreshCw className="w-3.5 h-3.5" /> Try Google again
                        </button>
                        <button
                            type="button"
                            data-testid="auth-fallback-magic"
                            onClick={() => setShowMagicForm(true)}
                            className="btn-ghost-tactical text-xs inline-flex items-center gap-2 justify-center"
                        >
                            <Mail className="w-3.5 h-3.5" /> Email me a sign-in link
                        </button>
                    </div>
                )}

                {showMagicForm && !magicSent && (
                    <form
                        onSubmit={sendMagicLink}
                        data-testid="auth-magic-form"
                        className="w-full max-w-sm border border-zinc-900 bg-zinc-950 p-6 space-y-4"
                    >
                        <button
                            type="button"
                            onClick={() => setShowMagicForm(false)}
                            className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 hover:text-white inline-flex items-center gap-1.5"
                        >
                            <ArrowLeft className="w-3 h-3" /> Back
                        </button>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Email sign-in</div>
                        <h2 className="font-display text-2xl tracking-wider text-white uppercase leading-tight">
                            We'll email you a link
                        </h2>
                        <input
                            type="email"
                            required
                            autoFocus
                            value={magicEmail}
                            onChange={(e) => setMagicEmail(e.target.value)}
                            placeholder="you@email.com"
                            data-testid="auth-magic-email"
                            className="w-full bg-black border border-zinc-800 px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-red"
                        />
                        <button
                            type="submit"
                            disabled={magicSending || !magicEmail}
                            data-testid="auth-magic-send"
                            className="btn-tactical w-full text-xs inline-flex items-center justify-center gap-2 disabled:opacity-40"
                        >
                            {magicSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                            Send me a sign-in link
                        </button>
                        <p className="text-[10px] tracking-wider text-zinc-500 leading-relaxed">
                            The link is valid for 15 minutes and works once. Check spam if it doesn't arrive.
                        </p>
                    </form>
                )}

                {magicSent && (
                    <div
                        data-testid="auth-magic-sent"
                        className="w-full max-w-sm border border-emerald-900/60 bg-emerald-950/20 p-6 text-center"
                    >
                        <Check className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
                        <div className="font-display text-2xl tracking-wider text-white uppercase">Link sent</div>
                        <p className="text-zinc-200 text-sm mt-2 leading-relaxed">
                            We just emailed a one-time sign-in link to <span className="text-white">{magicEmail}</span>. It expires in 15 minutes.
                        </p>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            data-testid="auth-callback"
            className="min-h-screen bg-brand-obsidian text-white flex flex-col items-center justify-center"
        >
            <Loader2 className="w-8 h-8 text-brand-red animate-spin" />
            <div className="mt-5 text-[10px] tracking-[0.3em] uppercase text-zinc-300">
                Signing you in
            </div>
        </div>
    );
}
